import { t } from '../../i18n';
import type { TextInputPopupHandle } from '../TextInputPopup';
import { createMailboxRow } from './domFactories';
import { compactMessagePreview, formatTimestamp, getThirdOption } from './mailboxData';
import { renderIncomingTradeReviewModal, renderOutgoingTradeReviewModal } from './tradeUi';
import type { MailboxDisplay, MailboxTab, TradeGridItem } from './types';

type TradeReviewItems = { myItems: TradeGridItem[]; theirItems: TradeGridItem[] };

interface MailboxTabState {
    activeMailboxTab: MailboxTab;
    messageRows: MailboxDisplay[];
    incomingTradeRows: MailboxDisplay[];
    outgoingTradeRows: MailboxDisplay[];
    incomingTradeReview: MailboxDisplay | null;
    incomingTradeReviewItems: TradeReviewItems | null;
    outgoingTradeReview: MailboxDisplay | null;
    outgoingTradeReviewItems: TradeReviewItems | null;
}

interface MailboxTabActions {
    openTradeComposer: (targetName: string) => Promise<void> | void;
    openIncomingTradeReview: (mail: MailboxDisplay) => Promise<void> | void;
    openOutgoingTradeReview: (mail: MailboxDisplay) => void;
    handleDeleteMail: (mailId: number) => void;
    handleCancelOutgoingTrade: (mailId: number) => void;
    handleAcceptTrade: (mailId: number) => Promise<boolean>;
    handleRejectTrade: (mailId: number) => Promise<boolean>;
    closeIncomingTradeReview: () => void;
    closeOutgoingTradeReview: () => void;
}

interface RenderMailboxTabParams {
    list: HTMLDivElement;
    footer: HTMLDivElement;
    inputPopup: TextInputPopupHandle;
    state: MailboxTabState;
    actions: MailboxTabActions;
}

export function renderMailboxTab(params: RenderMailboxTabParams): void {
    const { list, footer, inputPopup, state, actions } = params;
    const {
        activeMailboxTab,
        messageRows,
        incomingTradeRows,
        outgoingTradeRows,
        incomingTradeReview,
        incomingTradeReviewItems,
        outgoingTradeReview,
        outgoingTradeReviewItems,
    } = state;
    const {
        openTradeComposer,
        openIncomingTradeReview,
        openOutgoingTradeReview,
        handleDeleteMail,
        handleCancelOutgoingTrade,
        handleAcceptTrade,
        handleRejectTrade,
        closeIncomingTradeReview,
        closeOutgoingTradeReview,
    } = actions;

    if (activeMailboxTab === 'Messages') {
        if (messageRows.length === 0) {
            appendEmptyState(list, t('userMenu.mailbox.emptyMessages'));
            return;
        }

        messageRows.forEach((mail) => {
            const item = createMailboxRow({
                senderLabel: mail.fromDisplayName,
                timeLabel: formatTimestamp(mail.timeSent),
                thirdOption: compactMessagePreview(mail.msg),
            });

            const deleteBtn = createMailboxActionButton(t('userMenu.mailbox.delete'), '#374151', '#d1d5db');
            deleteBtn.addEventListener('click', () => {
                handleDeleteMail(mail.mailId);
            });

            item.appendChild(deleteBtn);
            list.appendChild(item);
        });
        return;
    }

    if (activeMailboxTab === 'IncomingTrades') {
        footer.appendChild(createNewTradeButton(inputPopup, openTradeComposer));

        if (incomingTradeRows.length === 0) {
            appendEmptyState(list, t('userMenu.mailbox.emptyIncomingTrades'));
            return;
        }

        incomingTradeRows.forEach((mail) => {
            const item = createMailboxRow({
                senderLabel: mail.fromDisplayName,
                timeLabel: formatTimestamp(mail.timeSent),
                thirdOption: getThirdOption(mail),
            });

            const reviewBtn = createMailboxActionButton(t('userMenu.mailbox.reviewTrade'), '#2563eb');
            reviewBtn.addEventListener('click', () => {
                void openIncomingTradeReview(mail);
            });

            item.appendChild(reviewBtn);
            list.appendChild(item);
        });

        if (incomingTradeReview) {
            list.appendChild(renderIncomingTradeReviewModal({
                mail: incomingTradeReview,
                items: incomingTradeReviewItems,
                onAccept: handleAcceptTrade,
                onReject: handleRejectTrade,
                onClose: closeIncomingTradeReview,
            }));
        }
        return;
    }

    const newTradeBtn = createNewTradeButton(inputPopup, openTradeComposer);

    if (outgoingTradeRows.length === 0) {
        appendEmptyState(list, t('userMenu.mailbox.emptyOutgoingTrades'));
    } else {
        outgoingTradeRows.forEach((mail) => {
            const item = createMailboxRow({
                senderLabel: t('userMenu.mailbox.toLine', { name: mail.toDisplayName }),
                timeLabel: formatTimestamp(mail.timeSent),
                thirdOption: getThirdOption(mail),
            });

            const reviewBtn = createMailboxActionButton(t('userMenu.mailbox.reviewTrade'), '#2563eb');
            reviewBtn.addEventListener('click', () => {
                openOutgoingTradeReview(mail);
            });

            const cancelBtn = createMailboxActionButton(t('userMenu.mailbox.cancelTrade'), '#7c2d12');
            cancelBtn.addEventListener('click', () => {
                handleCancelOutgoingTrade(mail.mailId);
            });

            item.appendChild(reviewBtn);
            item.appendChild(cancelBtn);
            list.appendChild(item);
        });
    }

    if (outgoingTradeReview) {
        list.appendChild(renderOutgoingTradeReviewModal({
            mail: outgoingTradeReview,
            items: outgoingTradeReviewItems,
            onClose: closeOutgoingTradeReview,
        }));
    }

    footer.appendChild(newTradeBtn);
}

function appendEmptyState(list: HTMLDivElement, message: string): void {
    const empty = document.createElement('div');
    empty.textContent = message;
    Object.assign(empty.style, { textAlign: 'center', color: '#6b7280', marginTop: '32px' });
    list.appendChild(empty);
}

function createMailboxActionButton(label: string, backgroundColor: string, color = '#fff'): HTMLButtonElement {
    const button = document.createElement('button');
    button.textContent = label;
    button.type = 'button';
    Object.assign(button.style, {
        padding: '4px 8px',
        backgroundColor,
        color,
        border: 'none',
        borderRadius: '3px',
        cursor: 'pointer',
        fontSize: '0.8rem',
    });
    return button;
}

function createNewTradeButton(
    inputPopup: TextInputPopupHandle,
    openTradeComposer: (targetName: string) => Promise<void> | void
): HTMLButtonElement {
    const newTradeBtn = createMailboxActionButton(t('userMenu.mailbox.newTrade'), '#2563eb');
    Object.assign(newTradeBtn.style, {
        padding: '6px 12px',
    });
    newTradeBtn.addEventListener('click', () => {
        inputPopup.show(
            t('userMenu.mailbox.newTradePrompt'),
            async (text) => {
                await openTradeComposer(text);
            },
            '',
            { modal: false }
        );
    });
    return newTradeBtn;
}
